import { RoleCodeById } from '®shared/permissions/roles';

import { AddResourceSearchType } from './add-resource-search-type';
import { DiscoverySystemConfig } from './discovery-system-config';
import { FeatureFlags } from './feature-flags';
import { HierarchyConfig } from './hierarchy-config';
import { LibraryConfig } from './library-config';
import { SessionConfig } from './session-config';

/**
 * Application configuration must meet certain criteria in order for the app
 * to have access to the authentication and API locations.
 **/
export abstract class AppConfig {
  // These properties are filled in for each tenant by the webserver
  apiEndpoint: string;
  beta?: string;
  error?: string;
  isEmbeddedMode?: boolean;
  ltiRelinkUrl?: string;
  personaEndpoint: string;
  oauthAppId: string;
  personaOrigin: string;
  talisLoginProvider: string;
  zendeskOutboundKey?: string;
  session?: SessionConfig;
  bookmarkingExtensionUrl?: string;
  tenant: {
    anonymousAccessToken?: string;
    conceptBaseUri: string;
    httpsBaseUri: string;
    name: string;
    roleMapping: { [roleCode: string]: string[] };
    roleCodesByRoleId: RoleCodeById;
    shortCode: string;
    v2DashboardEnabled?: boolean;
    reviewsUrl: string;
    elevateApiUrl: string;
    featureFlags: FeatureFlags;
    library: LibraryConfig;
    hierarchy: HierarchyConfig;
    discoverySystems?: DiscoverySystemConfig[];
    isUnstableResourceTypesEnabled?: boolean;
  };

  getAddResourceSearchType(
    previewDiscoverySystemId: string | null,
  ): AddResourceSearchType {
    const discoverySystem = this.getDiscoverySystem(previewDiscoverySystemId);

    if (discoverySystem) {
      return AddResourceSearchType.DISCOVERY;
    } else if (this.tenant.featureFlags.isAddFromWorksEnabled) {
      return AddResourceSearchType.NBD_BOOK_SEARCH;
    }

    return AddResourceSearchType.OFF;
  }

  getDiscoverySystem(
    previewDiscoverySystemId: string | null,
  ): DiscoverySystemConfig | undefined {
    const findDiscoverySystemById = (discoverySystemId: string) =>
      this.tenant.discoverySystems?.find(
        discoverySystem => discoverySystem.id === discoverySystemId,
      );

    // Priority given to a provided `previewDiscoverySystemId`...
    if (previewDiscoverySystemId) {
      const previewDiscoverySystemConfig = findDiscoverySystemById(
        previewDiscoverySystemId,
      );
      if (previewDiscoverySystemConfig) {
        return previewDiscoverySystemConfig;
      }
    }

    // ...then to the feature flag discovery system ID...
    if (this.tenant.featureFlags.discoverySystemId) {
      const tenantDiscoverySystemConfig = findDiscoverySystemById(
        this.tenant.featureFlags.discoverySystemId,
      );
      if (tenantDiscoverySystemConfig) {
        return tenantDiscoverySystemConfig;
      }
    }

    // ...there is no default at present.
    return undefined;
  }

  get isAnLti1p0EmbeddedSession(): boolean {
    if (this.ltiLaunchId !== null) {
      return false;
    }

    if (this.session && this.session.isAnLti1p0EmbeddedSession !== undefined) {
      return this.session.isAnLti1p0EmbeddedSession;
    }

    if (this.isEmbeddedMode) {
      return this.isEmbeddedMode;
    }

    return false;
  }

  get ltiLaunchId(): string | null {
    if (this.session && this.session.lti1p3LaunchId !== undefined) {
      return this.session.lti1p3LaunchId;
    }
    return null;
  }

  get isAnLti1p3Session(): boolean {
    return Boolean(this.ltiLaunchId);
  }

  get isUnstableResourceTypesEnabled(): boolean {
    return this.tenant.featureFlags.isUnstableResourceTypesEnabled ?? false;
  }

  /**
   * Fetch a member@tenant-login-provider scope.
   *
   * @returns {string} - member@tenant-login-provider scope for the tenant
   **/
  scopeMemberAtTenant(): string {
    return `member@${this.tenant.shortCode}`;
  }

  /**
   * Fetch an su@tenant scope.
   *
   * @returns {string} - su@tenant scope for the tenant
   **/
  scopeSuAtTenant(): string {
    return `su@${this.tenant.shortCode}`;
  }

  /**
   * Fetch an su scope.
   *
   * @returns {string} - su scope
   **/
  scopeSu(): string {
    return 'su';
  }

  /**
   * Fetch a list of scopes considered "logged in" (as opposed to anonymous)
   *
   * @returns {string[]} - list of authenticated scopes for the tenant
   **/
  scopesForLoggedInUsers(): string[] {
    if (this.tenant.anonymousAccessToken) {
      return [];
    }

    return [this.scopeMemberAtTenant()];
  }

  /**
   * Fetch the URL for error reporting, defaulting to a tenant-relative error page
   *
   * @returns {string} - path to error page.
   **/
  errorUrl(): string {
    return this.error || `/3/${this.tenant.shortCode}/error.html`;
  }
}

/**
 * Exception type covering configuration errors
 **/
export class InvalidConfigException extends Error {
  constructor(config, context) {
    const message = `
== Shipshape is misconfigured! ==
Please check that \`${context}\` is a valid Shipshape configuration.

Found:
${JSON.stringify(config, null, '  ')}
    `;

    super(message);
    this.message = message;
  }
}
